import type { FC } from 'react';
import type { StyleProp, TextStyle } from 'react-native';
import { StyleSheet } from 'react-native';
import type { TextProps } from 'react-native-paper';
import { Text } from 'react-native-paper';

import { VerifiedFilledIcon } from '@/assets';
import { Row } from '@/shared/components/layout/Row';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const ICON_SIZE = 16;

type Props = {
  name?: string;
  /** Shows the red verified badge (organizations only). */
  verified?: boolean;
  variant?: TextProps<string>['variant'];
  textStyle?: StyleProp<TextStyle>;
  numberOfLines?: number;
  /**
   * Called when the name is tapped. When provided, the name becomes
   * pressable — used to open the organization profile from post headers.
   */
  onPress?: () => void;
};

// Badge is a flex sibling (not inline inside the Text) because inline SVG
// baseline alignment is unreliable across iOS versions — on iOS 26 the badge
// visibly dropped below the text center.
const styles = StyleSheet.create({
  wrapRow: { alignItems: 'center', flexWrap: 'wrap' },
  singleLineRow: { alignItems: 'center', gap: 4 },
  trailingGroup: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  shrinkText: { flexShrink: 1 },
  icon: { flexShrink: 0 }
});

/**
 * Name with an optional red verified badge — shared by the feed card, the
 * post-detail header and the comment. Organizations are shown as verified.
 */
export const VerifiedName: FC<Props> = ({
  name,
  verified = false,
  variant = 'titleMedium',
  textStyle,
  numberOfLines,
  onPress
}) => {
  const theme = useAppTheme();

  const renderText = (
    value: string | undefined,
    extraStyle?: StyleProp<TextStyle>
  ) => (
    <Text
      variant={variant}
      style={[extraStyle, textStyle]}
      numberOfLines={numberOfLines}
      onPress={onPress}
      suppressHighlighting
    >
      {value}
    </Text>
  );

  if (!verified) {
    return renderText(name);
  }

  const icon = (
    <VerifiedFilledIcon
      fill={theme.colors.error}
      width={ICON_SIZE}
      height={ICON_SIZE}
      style={styles.icon}
    />
  );

  // Truncating callers (e.g. nav-bar titles) want a single row that
  // ellipsises the name before pushing the badge away.
  if (numberOfLines) {
    return (
      <Row style={styles.singleLineRow}>
        {renderText(name, styles.shrinkText)}
        {icon}
      </Row>
    );
  }

  // When the name wraps, bundle the trailing word with the badge so the
  // badge never lands on a line by itself.
  const trimmed = (name ?? '').trimEnd();
  const lastSpace = trimmed.lastIndexOf(' ');
  const leading = lastSpace === -1 ? '' : trimmed.slice(0, lastSpace + 1);
  const trailing = lastSpace === -1 ? trimmed : trimmed.slice(lastSpace + 1);

  return (
    <Row style={styles.wrapRow}>
      {leading !== '' && renderText(leading)}
      <Row style={styles.trailingGroup}>
        {renderText(trailing)}
        {icon}
      </Row>
    </Row>
  );
};
